import { useAgentComplianceReviewOpenPlatformMutation } from '@client/graphql/__generated__/main-operations';
import {
  AgentComplianceReviewByShareLinkTokenQuery,
  PlatformRequirementIssueStatus,
} from '@client/graphql/__generated__/types';
import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createSearchParams, useNavigate } from 'react-router-dom';
import {
  CompliancePlatformRequirementIssue,
  ComplianceSuggestion,
} from '../components/types';
import {
  buildChecklistItems,
  ChecklistItem,
  findItemForDocument,
} from '../utils/buildChecklistItems';
import {
  groupByContractDocument,
  shouldShowContractDocumentHeadings,
} from '../utils/groupByContractDocument';
import { useAgentComplianceReviewDocument } from './useAgentComplianceReviewDocument';
import { useAgentComplianceReviewIgnoreAndResubmit } from './useAgentComplianceReviewIgnoreAndResubmit';
import { useAgentComplianceReviewIgnorePlatformRequirementIssue } from './useAgentComplianceReviewIgnorePlatformRequirementIssue';
import { useAgentComplianceReviewIgnoreSuggestion } from './useAgentComplianceReviewIgnoreSuggestion';
import { useAgentComplianceReviewPlatformUrl } from './useAgentComplianceReviewPlatformUrl';
import { useAgentDocumentValidation } from './useAgentDocumentValidation';
import {
  ChecklistItemDocDetail,
  useChecklistItemDocuments,
} from './useChecklistItemDocuments';

type AgentComplianceReviewOverview =
  AgentComplianceReviewByShareLinkTokenQuery['agentComplianceReviewByShareLinkToken'];

const PDF_URL_REFRESH_INTERVAL_MS = 1000 * 60 * 55;

const buildSanitizedPdfLoadError = (error: Error): Error => {
  const sanitizedError = new Error(
    'Agent compliance review PDF failed to load'
  );
  sanitizedError.name = error.name || 'PdfLoadError';

  return sanitizedError;
};

interface UseAgentComplianceReviewArgs {
  overview: AgentComplianceReviewOverview;
  /** Currently selected document tab (from the `?documentId=` query param). */
  selectedDocumentId: string | undefined;
  /** Share-link token (`?token=` query param), preserved across navigation. */
  token: string | undefined;
  /** Folder UUID (route param) the review belongs to. */
  ingestionFolderUuid: string | undefined;
  /**
   * When true, auto-select the first Needs-Review checklist item (its first
   * document tab) on load if nothing is selected. The desktop layout enables
   * this (sidebar + detail are both visible); the mobile layout leaves the
   * agent on the overview list and opts out.
   */
  autoSelectFirstNeedsReview?: boolean;
}

const isActionablePlatformResubmitIssue = (
  issue: CompliancePlatformRequirementIssue
): boolean =>
  issue.isAgentActionable &&
  (issue.status === PlatformRequirementIssueStatus.OPEN ||
    issue.status === PlatformRequirementIssueStatus.IGNORED_BY_AGENT);

/**
 * Builds the prepopulated resubmit note from every tab in the checklist item,
 * grouped by file name. A multi-document item lists each file's issues under a
 * `File.pdf:` header; a single-document item keeps the flat bullet list (the
 * pre-redesign shape). Files with no issues are omitted. When the agent gave a
 * reason while inline-ignoring a suggestion, that reason is appended to the
 * suggestion's line (`{message} — {reason}`) so the reviewer sees the rationale
 * inline. The backend appends the "– {Agent} via Indigo" signature, so it is
 * not included here.
 */
const buildResubmitMessage = (docDetails: ChecklistItemDocDetail[]): string => {
  const seenPlatformIssueIds = new Set<string>();
  const fileSections = docDetails
    .map((detail) => {
      const platformLines = detail.platformRequirementIssues
        .filter(isActionablePlatformResubmitIssue)
        // Activity-scoped checklist issues surface on multiple tabs; list each
        // once, under the first file it appears on.
        .filter((issue) => {
          if (seenPlatformIssueIds.has(issue.publicIssueId)) {
            return false;
          }
          seenPlatformIssueIds.add(issue.publicIssueId);

          return true;
        })
        .map((issue) => `• Checklist suggestion: ${issue.message}`);

      // Head each form's bullets with its name when the PDF holds more than one
      // contract document, mirroring the review rail — the note carries no page
      // numbers, so the form name is the only thing telling two identically
      // worded suggestions apart (BLO-13411). Prefixing every bullet said the
      // same thing but repeated a long label on each line.
      const suggestionGroups = groupByContractDocument(detail.suggestions);
      const showFormHeadings = shouldShowContractDocumentHeadings(
        suggestionGroups,
        detail.contractDocumentCount
      );

      // Fold the agent's inline ignore reason into the bullet so the reviewer
      // sees why it was crossed out, without adding a separate section. Applied
      // in both layouts below so a grouped note reads the same as a flat one.
      const toBullet = (suggestion: ComplianceSuggestion): string => {
        const reason = suggestion.ignoreReason?.trim();

        return reason != null && reason !== ''
          ? `• ${suggestion.message} — ${reason}`
          : `• ${suggestion.message}`;
      };

      const suggestionLines = showFormHeadings
        ? suggestionGroups.flatMap((group) => [
            ...(group.label ? [`${group.label}:`] : []),
            ...group.rows.map(toBullet),
          ])
        : detail.suggestions.map(toBullet);

      return {
        name: detail.documentName,
        lines: [...suggestionLines, ...platformLines],
      };
    })
    .filter((section) => section.lines.length > 0);

  const intro = "Hi, I've resubmitted this checklist item for review.";

  if (fileSections.length === 0) {
    return [intro, 'Thanks!'].join('\n\n');
  }

  // `section.lines` arrive already rendered — bullets carry their own "• " and
  // per-form headings deliberately do not, so a heading reads as a label above
  // the bullets it introduces rather than as another issue.
  const isMultiFile = docDetails.length > 1;
  const body = isMultiFile
    ? `Here are the issues that I think are not relevant:\n${fileSections
        .map((section) => `${section.name}:\n${section.lines.join('\n')}`)
        .join('\n\n')}`
    : `Here are the issues that I think are not relevant:\n${fileSections[0].lines.join(
        '\n'
      )}`;

  return [intro, body, 'Thanks!'].join('\n\n');
};

/**
 * Shared controller for the agent compliance review layouts. The page is
 * organized around SkySlope checklist items (an activity + the ingestion
 * documents under it). The left rail lists checklist items; selecting one opens
 * its documents as tabs in the center; the agent acts on the whole item.
 *
 * Owns: the checklist-item grouping, the active item / tabs derivation, the
 * per-document detail load for the active tab, derived validation, the
 * focused-field state, the token-bound ignore / resubmit mutations, the
 * selected PDF load-error recovery / in-flight document refetch dedupe, the
 * scheduled PDF URL refresh, the checklist-level resubmit (ignores every tab's
 * suggestions + the activity's checklist issues, note grouped by file), and the
 * URL-driven tab selection.
 *
 * Selection is URL-driven: {@link onSelectDocument} navigates to
 * `compliance-review/:ingestionFolderUuid?token=…&documentId=…`, and
 * {@link onClearSelection} drops the `documentId` to return to the overview
 * (the mobile back affordance).
 */
export const useAgentComplianceReview = ({
  overview,
  selectedDocumentId,
  token,
  ingestionFolderUuid,
  autoSelectFirstNeedsReview = false,
}: UseAgentComplianceReviewArgs) => {
  const navigate = useNavigate();

  const { document, loading, error, refetchDocument } =
    useAgentComplianceReviewDocument({
      token,
      ingestionFolderUuid,
      ingestionDocumentId: selectedDocumentId,
    });
  const pdfUrl = document?.pdfUrl;
  const refreshingPdfUrlRef = useRef<string | null>(null);

  useEffect(() => {
    refreshingPdfUrlRef.current = null;
  }, [pdfUrl, selectedDocumentId]);

  const refreshSelectedDocument = useCallback(
    async (
      source: 'pdf-load-recovery' | 'scheduled-url-refresh'
    ): Promise<boolean> => {
      try {
        await refetchDocument();

        return true;
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            source: `agent-compliance-review-${source}`,
          },
          extra: {
            ingestionFolderUuid,
            ingestionDocumentId: selectedDocumentId,
          },
        });

        return false;
      }
    },
    [ingestionFolderUuid, refetchDocument, selectedDocumentId]
  );

  const onPdfLoadError = useCallback(
    (error: Error) => {
      if (!pdfUrl || refreshingPdfUrlRef.current === pdfUrl) {
        return;
      }

      Sentry.captureException(buildSanitizedPdfLoadError(error), {
        tags: {
          source: 'agent-compliance-review-pdf-load',
        },
        extra: {
          pdfLoadErrorName: error.name,
          ingestionFolderUuid,
          ingestionDocumentId: selectedDocumentId,
        },
      });

      refreshingPdfUrlRef.current = pdfUrl;
      void refreshSelectedDocument('pdf-load-recovery').finally(() => {
        if (refreshingPdfUrlRef.current === pdfUrl) {
          refreshingPdfUrlRef.current = null;
        }
      });
    },
    [ingestionFolderUuid, pdfUrl, refreshSelectedDocument, selectedDocumentId]
  );

  useEffect(() => {
    if (!pdfUrl) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void refreshSelectedDocument('scheduled-url-refresh');
    }, PDF_URL_REFRESH_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [pdfUrl, refreshSelectedDocument]);

  // The platform deep-link ("Fix in SkySlope") is fetched ON DEMAND — only once
  // a document is open. Skipped until a document is selected.
  const { platformUrl } = useAgentComplianceReviewPlatformUrl({
    token,
    ingestionFolderUuid,
    skip: selectedDocumentId == null,
  });
  const [trackPlatformOpen] = useAgentComplianceReviewOpenPlatformMutation({
    context: { skipGlobalErrorHandler: true },
  });
  const onOpenPlatform = useCallback((): void => {
    if (
      token == null ||
      ingestionFolderUuid == null ||
      selectedDocumentId == null
    ) {
      return;
    }

    void trackPlatformOpen({
      variables: {
        input: {
          token,
          ingestionFolderUuid,
          ingestionDocumentId: selectedDocumentId,
        },
      },
    }).catch((error: unknown) => {
      Sentry.captureException(error, {
        tags: { source: 'agent-compliance-review-platform-open' },
        extra: {
          ingestionFolderUuid,
          ingestionDocumentId: selectedDocumentId,
        },
      });
    });
  }, [ingestionFolderUuid, selectedDocumentId, token, trackPlatformOpen]);

  // Group the overview documents into SkySlope checklist items and split them
  // into the Needs-Review and Other buckets the rail renders.
  const { items, needsReviewItems, otherItems } = useMemo(
    () => buildChecklistItems(overview.documents),
    [overview.documents]
  );

  // The checklist item the selected tab belongs to, and that item's documents
  // (the tabs). The item is the unit the title bar / actions / nav operate on.
  const activeItem = useMemo(
    () => findItemForDocument(items, selectedDocumentId),
    [items, selectedDocumentId]
  );
  const activeItemDocs = useMemo(() => activeItem?.docs ?? [], [activeItem]);

  const navigateToDocument = useCallback(
    (ingestionDocumentId: string, replace = false) => {
      const search = createSearchParams({
        ...(token != null ? { token } : {}),
        documentId: ingestionDocumentId,
      }).toString();
      navigate(`/compliance-review/${ingestionFolderUuid}?${search}`, {
        replace,
      });
    },
    [navigate, token, ingestionFolderUuid]
  );

  const onSelectDocument = useCallback(
    (ingestionDocumentId: string) => navigateToDocument(ingestionDocumentId),
    [navigateToDocument]
  );

  // Drop the `?documentId=` selection (keeping the token) to return to the
  // overview. Backs the mobile breadcrumb's property/back affordance.
  const onClearSelection = useCallback(() => {
    const search =
      token != null ? `?${createSearchParams({ token }).toString()}` : '';
    navigate(`/compliance-review/${ingestionFolderUuid}${search}`);
  }, [navigate, token, ingestionFolderUuid]);

  // Auto-select the first Needs-Review item's first tab on load (desktop only).
  // Replace history so the browser back button doesn't bounce to the empty
  // overview. Fires once: once a tab is selected the guard stops it re-running.
  useEffect(() => {
    if (
      !autoSelectFirstNeedsReview ||
      selectedDocumentId != null ||
      needsReviewItems.length === 0
    ) {
      return;
    }
    navigateToDocument(needsReviewItems[0].docs[0].ingestionDocumentId, true);
  }, [
    autoSelectFirstNeedsReview,
    selectedDocumentId,
    needsReviewItems,
    navigateToDocument,
  ]);

  // ALL field ids of the suggestion/check row the user clicked, not just the
  // first: one validation issue can span several fields (e.g. an initials rule
  // matching a field per page), and the viewer highlights and captions the whole
  // set as one issue. Lifted here so the suggestion/check lists and the
  // DocumentViewer stay in sync.
  const [activeFieldIds, setActiveFieldIds] = useState<string[]>([]);

  // Clear the focused fields when the selected document changes.
  useEffect(() => {
    setActiveFieldIds([]);
  }, [selectedDocumentId]);

  const onSelectFields = useCallback((fieldIds: string[]) => {
    setActiveFieldIds(fieldIds);
  }, []);

  // Derive the active tab's suggestions / checks / field highlights client-side.
  const validation = useAgentDocumentValidation(document);

  // Bumped after an inline ignore/undo so the per-tab details are re-read. They
  // live in React state (fetched imperatively), so they do not react to the
  // cache patch on their own — without this the resubmit note is composed from a
  // pre-ignore snapshot and omits the agent's reason.
  const [ignoredSuggestionsVersion, setIgnoredSuggestionsVersion] = useState(0);

  // Load EVERY tab's detail for the active item so the checklist-level resubmit
  // can compose the file-grouped note and ignore the non-active tabs' suggestions.
  const { docDetails: activeItemDocDetails, loading: itemDocsLoading } =
    useChecklistItemDocuments({
      token,
      ingestionFolderUuid,
      docs: activeItemDocs,
      refreshKey: ignoredSuggestionsVersion,
    });

  // Token-bound mutations.
  const [ignore, { loading: isIgnoring }] =
    useAgentComplianceReviewIgnoreSuggestion({ token, ingestionFolderUuid });
  const [ignorePlatformIssue, { loading: isIgnoringPlatformIssue }] =
    useAgentComplianceReviewIgnorePlatformRequirementIssue({
      token,
      ingestionFolderUuid,
    });
  const [ignoreAndResubmit, { loading: isSubmitting }] =
    useAgentComplianceReviewIgnoreAndResubmit({ token, ingestionFolderUuid });

  const ingestionDocumentValidationId =
    document?.ingestionDocumentValidationId ?? undefined;

  // Hide a single document-level suggestion on the active tab, with the agent's
  // optional ignore reason.
  const onIgnoreDocumentSuggestion = useCallback(
    async (suggestion: ComplianceSuggestion, message: string | null) => {
      if (selectedDocumentId == null) {
        return;
      }
      // Awaited (not `void`) so the inline-ignore modal can keep its spinner up
      // and stay open if the write fails.
      try {
        await ignore({
          ingestionDocumentId: selectedDocumentId,
          ingestionDocumentValidationId,
          contractDocumentUuid: suggestion.contractDocumentUuid ?? undefined,
          ruleUuid: suggestion.ruleUuid ?? undefined,
          pageNumber: suggestion.pageNumbers?.[0],
          message,
        });
      } catch (error) {
        // Reported here (the modal only needs to know it failed) and rethrown
        // so the modal keeps itself open with the typed reason intact.
        Sentry.captureException(error, {
          tags: { source: 'agent-compliance-review-ignore-suggestion' },
          extra: {
            ingestionFolderUuid,
            ingestionDocumentId: selectedDocumentId,
            ruleUuid: suggestion.ruleUuid,
            contractDocumentUuid: suggestion.contractDocumentUuid,
          },
        });

        throw error;
      }
      // Re-read the per-tab details so the resubmit note picks up this ignore
      // (and its reason) instead of the snapshot taken before it.
      setIgnoredSuggestionsVersion((version) => version + 1);
    },
    [
      ignore,
      selectedDocumentId,
      ingestionDocumentValidationId,
      ingestionFolderUuid,
    ]
  );

  // Un-hide a previously AGENT-ignored active-tab suggestion (Undo).
  const onUndoIgnoreDocumentSuggestion = useCallback(
    async (suggestion: ComplianceSuggestion) => {
      if (selectedDocumentId == null) {
        return;
      }
      try {
        await ignore({
          ingestionDocumentId: selectedDocumentId,
          ingestionDocumentValidationId,
          contractDocumentUuid: suggestion.contractDocumentUuid ?? undefined,
          ruleUuid: suggestion.ruleUuid ?? undefined,
          pageNumber: suggestion.pageNumbers?.[0],
          isHidden: false,
        });
      } catch (error) {
        // Bumping the version on a failed write would re-read the details and
        // silently revert the optimistic un-ignore with no explanation, so
        // leave the cache patch in place and report the real cause instead.
        Sentry.captureException(error, {
          tags: { source: 'agent-compliance-review-undo-ignore' },
          extra: {
            ingestionFolderUuid,
            ingestionDocumentId: selectedDocumentId,
            ruleUuid: suggestion.ruleUuid,
            contractDocumentUuid: suggestion.contractDocumentUuid,
          },
        });

        return;
      }
      // Same reason as the ignore path: without a re-read the note would keep
      // listing this suggestion (and its reason) after it was un-ignored.
      setIgnoredSuggestionsVersion((version) => version + 1);
    },
    [
      ignore,
      selectedDocumentId,
      ingestionDocumentValidationId,
      ingestionFolderUuid,
    ]
  );

  // Hide a single folder-level suggestion.
  const onIgnoreFolderSuggestion = useCallback(
    (suggestion: ComplianceSuggestion) => {
      void ignore({
        ingestionFolderValidationId:
          suggestion.ingestionFolderValidationId ?? undefined,
        documentCategory: suggestion.documentCategory ?? undefined,
        documentForm: suggestion.documentForm ?? undefined,
        documentName: suggestion.documentName ?? undefined,
        ruleUuid: suggestion.ruleUuid ?? undefined,
      });
    },
    [ignore]
  );

  // Un-hide a previously ignored folder-level suggestion (Undo).
  const onUndoIgnoreFolderSuggestion = useCallback(
    (suggestion: ComplianceSuggestion) => {
      void ignore({
        ingestionFolderValidationId:
          suggestion.ingestionFolderValidationId ?? undefined,
        documentCategory: suggestion.documentCategory ?? undefined,
        documentForm: suggestion.documentForm ?? undefined,
        documentName: suggestion.documentName ?? undefined,
        ruleUuid: suggestion.ruleUuid ?? undefined,
        isHidden: false,
      });
    },
    [ignore]
  );

  // Checklist mismatches for the active tab — straight off the detail.
  const platformRequirementIssues: CompliancePlatformRequirementIssue[] =
    useMemo(
      () => document?.platformRequirementIssues ?? [],
      [document?.platformRequirementIssues]
    );

  // Hide a single checklist mismatch on the active tab.
  const onIgnorePlatformRequirementIssue = useCallback(
    (issue: CompliancePlatformRequirementIssue) => {
      if (selectedDocumentId == null) {
        return;
      }
      void ignorePlatformIssue({
        ingestionDocumentId: selectedDocumentId,
        publicIssueId: issue.publicIssueId,
        isHidden: true,
      });
    },
    [ignorePlatformIssue, selectedDocumentId]
  );

  // Un-hide a previously AGENT-ignored checklist mismatch (Undo).
  const onUndoIgnorePlatformRequirementIssue = useCallback(
    (issue: CompliancePlatformRequirementIssue) => {
      if (selectedDocumentId == null) {
        return;
      }
      void ignorePlatformIssue({
        ingestionDocumentId: selectedDocumentId,
        publicIssueId: issue.publicIssueId,
        isHidden: false,
      });
    },
    [ignorePlatformIssue, selectedDocumentId]
  );

  // Per-tab count of items still needing attention — non-ignored validation
  // suggestions + open actionable checklist issues — derived LIVE so the agent's
  // in-session ignores immediately drop the count (and clear the warning icon),
  // rather than the static server `openSuggestionCount`. The active tab reads
  // its live (cache-reactive) validation/issues; the other tabs read their
  // loaded detail, falling back to the server count until that detail loads.
  const tabSuggestionCounts = useMemo(() => {
    const openActionableIssueCount = (
      issues: CompliancePlatformRequirementIssue[]
    ): number =>
      issues.filter(
        (issue) =>
          issue.status === PlatformRequirementIssueStatus.OPEN &&
          issue.isAgentActionable
      ).length;

    const counts: Record<string, number> = {};
    for (const doc of activeItemDocs) {
      if (doc.ingestionDocumentId === selectedDocumentId) {
        counts[doc.ingestionDocumentId] =
          validation.suggestions.filter((suggestion) => !suggestion.isIgnored)
            .length + openActionableIssueCount(platformRequirementIssues);
        continue;
      }
      const detail = activeItemDocDetails.find(
        (item) => item.ingestionDocumentId === doc.ingestionDocumentId
      );
      counts[doc.ingestionDocumentId] =
        detail != null
          ? detail.suggestions.filter((suggestion) => !suggestion.isIgnored)
              .length +
            openActionableIssueCount(detail.platformRequirementIssues)
          : doc.openSuggestionCount;
    }

    return counts;
  }, [
    activeItemDocs,
    selectedDocumentId,
    validation.suggestions,
    platformRequirementIssues,
    activeItemDocDetails,
  ]);

  // The note is composed from every tab in the active item, grouped by file.
  const defaultResubmitMessage = useMemo(
    () => buildResubmitMessage(activeItemDocDetails),
    [activeItemDocDetails]
  );

  // Resubmit the WHOLE checklist item. One `ignoreAndResubmit` on the primary
  // (active) tab links every sibling document in the activity and ignores the
  // activity's open checklist issues; the other tabs' validation suggestions are
  // ignored per-document first (they are keyed to each document's own validation
  // id). Once it lands, auto-advance to the next item that still needs review.
  const onResubmit = useCallback(
    async (message: string) => {
      if (selectedDocumentId == null) {
        return;
      }

      const primaryDetail = activeItemDocDetails.find(
        (detail) => detail.ingestionDocumentId === selectedDocumentId
      );

      // Open (not-yet-ignored) suggestions per tab. The active tab's are sent in
      // the resubmit payload; the rest are ignored with their own validation id.
      const toIgnorePayload = (detail: ChecklistItemDocDetail) =>
        detail.suggestions
          .filter((suggestion) => !suggestion.isIgnored)
          .map((suggestion) => ({
            contractDocumentUuid: suggestion.contractDocumentUuid ?? undefined,
            ruleUuid: suggestion.ruleUuid ?? undefined,
            pageNumber: suggestion.pageNumbers?.[0],
          }));

      const primarySuggestionsToIgnore = primaryDetail
        ? toIgnorePayload(primaryDetail)
        : validation.suggestions
            .filter((suggestion) => !suggestion.isIgnored)
            .map((suggestion) => ({
              contractDocumentUuid:
                suggestion.contractDocumentUuid ?? undefined,
              ruleUuid: suggestion.ruleUuid ?? undefined,
              pageNumber: suggestion.pageNumbers?.[0],
            }));

      const primaryValidationId =
        primaryDetail?.ingestionDocumentValidationId ??
        ingestionDocumentValidationId;

      // Validation suggestions need a validation id to key their review rows.
      // Checklist-only resubmits do not, so let those through.
      if (
        primarySuggestionsToIgnore.length > 0 &&
        primaryValidationId == null
      ) {
        return;
      }

      // Every actionable open checklist issue across the activity, deduped — the
      // single resubmit call resolves the activity scope for all of them.
      const platformRequirementIssuePublicIdsToIgnore = [
        ...new Set(
          activeItemDocDetails
            .flatMap((detail) => detail.platformRequirementIssues)
            .filter(
              (issue) =>
                issue.status === PlatformRequirementIssueStatus.OPEN &&
                issue.isAgentActionable
            )
            .map((issue) => issue.publicIssueId)
        ),
      ];

      // Resolve the next Needs-Review item to advance to BEFORE the resubmit
      // (the whole activity becomes resubmitted): the next item after this one,
      // wrapping to the first remaining item when this was the last.
      const activeIndex = needsReviewItems.findIndex(
        (item) => item.id === activeItem?.id
      );
      const nextItem =
        needsReviewItems.find((_item, index) => index > activeIndex) ??
        needsReviewItems.find((item) => item.id !== activeItem?.id);

      // The other tabs' open suggestions ride along in the SAME resubmit call so
      // the backend hides them in one transaction with the resubmission (atomic
      // item-level resubmit). Only tabs with a validation id + open suggestions
      // are included.
      const additionalDocumentSuggestionsToIgnore = activeItemDocDetails
        .filter(
          (detail) =>
            detail.ingestionDocumentId !== selectedDocumentId &&
            detail.ingestionDocumentValidationId != null
        )
        .map((detail) => ({
          ingestionDocumentId: detail.ingestionDocumentId,
          ingestionDocumentValidationId:
            detail.ingestionDocumentValidationId ?? undefined,
          suggestions: toIgnorePayload(detail),
        }))
        .filter((entry) => entry.suggestions.length > 0);

      await ignoreAndResubmit({
        ingestionDocumentId: selectedDocumentId,
        ingestionDocumentValidationId: primaryValidationId,
        message,
        suggestionsToIgnore: primarySuggestionsToIgnore,
        additionalDocumentSuggestionsToIgnore,
        platformRequirementIssuePublicIdsToIgnore,
      });

      if (nextItem != null) {
        onSelectDocument(nextItem.docs[0].ingestionDocumentId);
      }
    },
    [
      ignoreAndResubmit,
      selectedDocumentId,
      ingestionDocumentValidationId,
      validation.suggestions,
      activeItemDocDetails,
      activeItem,
      needsReviewItems,
      onSelectDocument,
    ]
  );

  const hasSelection = selectedDocumentId != null;

  // The selected tab's summary row (used by the mobile breadcrumb).
  const selectedDocSummary = overview.documents.find(
    (doc) => doc.ingestionDocumentId === selectedDocumentId
  );

  // Title-bar / action state is item-level: the item needs review if any of its
  // documents do, and it is resubmitted once any sibling has been.
  const itemNeedsReview = activeItem?.needsReview ?? false;
  const itemResubmitted = activeItem?.resubmittedAt != null;

  // The resubmit trigger waits for every tab's detail (the payload + note are
  // derived from all of them).
  const isResubmitReady =
    !itemDocsLoading &&
    activeItem != null &&
    activeItemDocDetails.length === activeItem.docs.length;

  // Checklist navigation cycles the Needs-Review items only; hidden entirely
  // when the active item is an "Other document".
  const activeNeedsReviewIndex = needsReviewItems.findIndex(
    (item) => item.id === activeItem?.id
  );
  const showChecklistNav = activeNeedsReviewIndex >= 0;
  const hasPrevChecklistItem = activeNeedsReviewIndex > 0;
  const hasNextChecklistItem =
    activeNeedsReviewIndex >= 0 &&
    activeNeedsReviewIndex < needsReviewItems.length - 1;

  const onSelectNextChecklistItem = useCallback(() => {
    if (!hasNextChecklistItem) {
      return;
    }
    onSelectDocument(
      needsReviewItems[activeNeedsReviewIndex + 1].docs[0].ingestionDocumentId
    );
  }, [
    hasNextChecklistItem,
    needsReviewItems,
    activeNeedsReviewIndex,
    onSelectDocument,
  ]);

  const onSelectPrevChecklistItem = useCallback(() => {
    if (!hasPrevChecklistItem) {
      return;
    }
    onSelectDocument(
      needsReviewItems[activeNeedsReviewIndex - 1].docs[0].ingestionDocumentId
    );
  }, [
    hasPrevChecklistItem,
    needsReviewItems,
    activeNeedsReviewIndex,
    onSelectDocument,
  ]);

  return {
    document,
    loading,
    error,
    onPdfLoadError,
    platformUrl,
    onOpenPlatform,
    activeFieldIds,
    validation,
    hasSelection,
    selectedDocSummary,
    // Checklist items + active item.
    needsReviewItems,
    otherItems,
    activeItem,
    activeItemDocs,
    tabSuggestionCounts,
    itemNeedsReview,
    itemResubmitted,
    isResubmitReady,
    platformRequirementIssues,
    isIgnoring,
    isIgnoringPlatformIssue,
    isSubmitting,
    defaultResubmitMessage,
    // Checklist navigation.
    showChecklistNav,
    hasPrevChecklistItem,
    hasNextChecklistItem,
    onSelectNextChecklistItem,
    onSelectPrevChecklistItem,
    onSelectFields,
    onSelectDocument,
    onClearSelection,
    onIgnoreDocumentSuggestion,
    onUndoIgnoreDocumentSuggestion,
    onIgnoreFolderSuggestion,
    onUndoIgnoreFolderSuggestion,
    onIgnorePlatformRequirementIssue,
    onUndoIgnorePlatformRequirementIssue,
    onResubmit,
  };
};

export type { ChecklistItem };
